import { useLang } from 'hooks/lang';

import { invoiceControlStatusConfig, projectStatusByCode, actions, isOwner } from 'entities';

import { Button, Column, Row } from 'components/Blocks';
import { CircleCheckIcon, SendIcon, WarningIcon } from 'components/Icons';

import { useProjectProfile } from 'pages/Projects/ProjectProfile/context';

import { useUser } from 'hooks/user';
import { useConfirmation } from 'providers/Confirmation';
import { useJustification } from 'providers/Justification';

import { cn } from 'styles';

export function SentToInvoice() {
  const user = useUser();
  const { project, updateProject } = useProjectProfile();
  const { t } = useLang();

  const { invoice, id, status } = project;

  const canSend = [
    !invoice.readyToInvoice,
    isOwner(project, user),
    projectStatusByCode[status].canSendToInvoice,
  ].every(Boolean);

  const { requestConfirmation } = useConfirmation();
  const { openJustificationModal } = useJustification();

  const sendToInvoice = actions.invoice.sendProjectToInvoice.use({
    onSuccess: () => {
      updateProject((old) => ({
        ...old,

        invoice: {
          ...old.invoice,

          readyToInvoice: true,

          status: old.invoice.type
            ? invoiceControlStatusConfig.readyToInvoice.code
            : old.invoice.status,
        },
      }));
    },
  });

  const send = (): void => {
    openJustificationModal({
      title: t('provide-context') as string,

      text: t('please-explain-sent-invoice'),

      charLimit: 500,

      onConfirm: async (comment) =>
        requestConfirmation({
          size: 'small',

          title: t('send-project-to-invoice') as string,

          text: t('please-be-sure-client-is-aware'),

          type: 'irreversible',

          onConfirm: async () => {
            await sendToInvoice.execute({
              project: id,
              comment,
            });
          },
        }),
    });
  };

  const { Icon, title, message, className } = invoice.readyToInvoice
    ? {
        Icon: <CircleCheckIcon color="success" />,
        title: t('sent-to-invoice'),
        message: t('this-project-has-been-sent-to-invoice'),
        className: 'border border-success bg-success-light/10',
      }
    : {
        Icon: <WarningIcon color="warning" />,
        title: t('not-sent-to-invoice'),
        message: t('project-was-not-sent-to-invoice-yet'),
        className: 'border border-warning',
      };

  return (
    <Column className={cn(className, 'w-full max-w-[400px] shrink-0 rounded-lg p-6')}>
      <Row gap="2.5">
        {Icon}

        <span className="font-semibold">{title}</span>
      </Row>

      <span className="text-sm">{message}</span>

      {!!invoice.sentToInvoiceComment && (
        <Column gap="2" className="text-sm leading-[2]">
          <strong>
            {t('comment-added-when-sent')}
            {': '}
          </strong>

          {invoice.sentToInvoiceComment}
        </Column>
      )}

      {canSend && (
        <Button onClick={send} variant="outlined" color="warning">
          <SendIcon />
          {t('send-to-invoice')}
        </Button>
      )}
    </Column>
  );
}
